import Button from "@cloudscape-design/components/button"
import { ReactNode } from "react"

interface UploadButtonProps {
  /**
   * Whether an upload is currently in progress.
   */
  uploading: boolean
  /**
   * Click handler to trigger file selection.
   */
  onClick: () => void
  /**
   * Button text when not uploading.
   */
  label?: string
  /**
   * Button text while uploading.
   */
  uploadingLabel?: string
  /**
   * Optional icon to display before label.
   */
  icon?: ReactNode
  /**
   * Button style variant.
   */
  variant?: "primary" | "secondary"
}

/**
 * Reusable upload button with loading state.
 *
 * Displays a spinner and disabled state while uploading is in progress. Used across protocol states
 * for PDF upload functionality. Wraps the Cloudscape Button component: `uploading` maps to its
 * `loading` state (which shows a spinner and blocks clicks), and the CDR `secondary` variant maps
 * to Cloudscape's `normal` variant.
 */
const UploadButton = ({
  uploading,
  onClick,
  label = "Upload",
  uploadingLabel = "Uploading...",
  icon,
  variant = "secondary",
}: UploadButtonProps) => (
  <Button
    variant={variant === "primary" ? "primary" : "normal"}
    loading={uploading}
    loadingText={uploadingLabel}
    iconSvg={icon}
    onClick={() => onClick()}
  >
    {uploading ? uploadingLabel : label}
  </Button>
)

export default UploadButton
